<script setup>
import {computed, onBeforeMount, ref} from 'vue'
import {endOfMonth, startOfMonth, format, isWithinInterval, endOfDay} from 'date-fns'
import DateRange from "@/components/UI/DateRange.vue";
import { ru } from 'date-fns/locale';
import {Api} from "@/service/api";
import {useRoute, useRouter} from "vue-router";
import ArrowSortSvg from "@/components/UI/svg/ArrowSortSvg.vue";
import ChevronUpDownSvg from "@/components/UI/svg/ChevronUpDownSvg.vue";
import ChevronLeftIcon from "@/components/UI/svg/ChevronLeftIcon.vue";
import NavigationBar from "@/components/NavigationBar.vue";

const route = useRoute();
const router = useRouter();
const bundles = ref([])
const sortField = ref('started_at');
const sortDirection = ref('desc');


const currentMonth = ref(new Date())
const startDate = ref(startOfMonth(currentMonth.value))
const endDate = ref(endOfMonth(currentMonth.value))

onBeforeMount(async () => {
    bundles.value = await Api.getWorkflowTransactions(route.params.workflow_id);
})

const sortedBundles = computed(() => {
    if (!bundles.value.length) return [];

    // Фильтруем данные по диапазону дат
    const filteredBundles = bundles.value.filter(bundle => {
        const bundleDate = new Date(bundle.started_at);
        return isWithinInterval(bundleDate, {
            start: startDate.value,
            end: endOfDay(endDate.value)
        });
    });

    // Сортируем отфильтрованные данные
    return filteredBundles.sort((a, b) => {
        let valueA, valueB;

        switch (sortField.value) {
            case 'started_at':
                valueA = new Date(a.started_at);
                valueB = new Date(b.started_at);
                break;
            case 'connection_id':
                valueA = a.connection_id;
                valueB = b.connection_id;
                break;
            case 'connection_name':
                valueA = a.connection_name?.toLowerCase();
                valueB = b.connection_name?.toLowerCase();
                break;
            case 'run_status':
                valueA = a.run_status?.toLowerCase();
                valueB = b.run_status?.toLowerCase();
                break;
            default:
                return 0;
        }

        if (valueA < valueB) return sortDirection.value === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection.value === 'asc' ? 1 : -1;
        return 0;
    });
});

const handleSort = (field) => {
    if (sortField.value === field) {
        // Переключаем направление сортировки, если поле то же самое
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        // Устанавливаем новое поле сортировки и направление по умолчанию
        sortField.value = field;
        sortDirection.value = 'desc';
    }
};

</script>

<template>
    <div class="w-full h-screen flex items-center justify-center relative">
        <NavigationBar class="absolute top-0 left-0"/>
        <div class="absolute top-10 left-40 size-10 rounded-full flex items-center justify-center hover:bg-gray-200 cursor-pointer transition-all duration-150"
             v-on:click="router.push({'name': 'FlowView', params: {'workflow_id': route.params.workflow_id}})"
        >
            <ChevronLeftIcon class="size-6" />
        </div>
        <div class="w-4/5 h-3/5 bg-white rounded-md mt-5 p-20">
            <div class="w-full flex justify-center items-start mb-8">
                <div class="font-bold text-2xl">Журнал транзакций</div>
                <DateRange
                    v-model:start-date="startDate"
                    v-model:end-date="endDate"
                    class="w-80 ml-auto"
                />
            </div>

            <div class="w-full flex flex-col mt-5">
                <!-- Заголовок таблицы (закрепленный) -->
                <div class="w-full grid grid-cols-6 gap-4 items-center h-12 bg-gray-100 px-6 rounded-t-lg sticky top-0 z-10">
                    <div
                        class="flex items-center justify-center font-bold text-center cursor-pointer hover:bg-gray-200 p-2 rounded"
                        @click="handleSort('started_at')"
                    >
                        <span>Дата последней выгрузки</span>
                        <div class="w-6 h-6 ml-1 relative">
                            <ChevronUpDownSvg
                                class="size-6 absolute top-0 left-0 transition-opacity"
                                :class="{ 'opacity-0': sortField === 'started_at' }"
                            />
                            <ArrowSortSvg
                                class="size-6 absolute top-0 left-0 transition-all"
                                :class="{
                                    'rotate-180': sortField === 'started_at' && sortDirection === 'desc',
                                    'opacity-0': sortField !== 'started_at'
                                }"
                            />
                        </div>
                    </div>
                    <div
                        class="flex items-center justify-center font-bold text-center cursor-pointer hover:bg-gray-200 p-2 rounded"
                        @click="handleSort('connection_id')"
                    >
                        <span>ID соединения</span>
                        <div class="w-6 h-6 ml-1 relative">
                            <ChevronUpDownSvg
                                class="size-6 absolute top-0 left-0 transition-opacity"
                                :class="{ 'opacity-0': sortField === 'connection_id' }"
                            />
                            <ArrowSortSvg
                                class="size-6 absolute top-0 left-0 transition-all"
                                :class="{
                                    'rotate-180': sortField === 'connection_id' && sortDirection === 'desc',
                                    'opacity-0': sortField !== 'connection_id'
                                }"
                            />
                        </div>
                    </div>
                    <div
                        class="flex items-center justify-center font-bold text-center cursor-pointer hover:bg-gray-200 p-2 rounded"
                        @click="handleSort('connection_name')"
                    >
                        <span>Название соединения</span>
                        <div class="w-6 h-6 ml-1 relative">
                            <ChevronUpDownSvg
                                class="size-6 absolute top-0 left-0 transition-opacity"
                                :class="{ 'opacity-0': sortField === 'connection_name' }"
                            />
                            <ArrowSortSvg
                                class="size-6 absolute top-0 left-0 transition-all"
                                :class="{
                                    'rotate-180': sortField === 'connection_name' && sortDirection === 'desc',
                                    'opacity-0': sortField !== 'connection_name'
                                }"
                            />
                        </div>
                    </div>
                    <div
                        class="flex items-center justify-center font-bold text-center cursor-pointer hover:bg-gray-200 p-2 rounded"
                        @click="handleSort('run_status')"
                    >
                        <span>Статус выгрузки</span>
                        <div class="w-6 h-6 ml-1 relative">
                            <ChevronUpDownSvg
                                class="size-6 absolute top-0 left-0 transition-opacity"
                                :class="{ 'opacity-0': sortField === 'run_status' }"
                            />
                            <ArrowSortSvg
                                class="size-6 absolute top-0 left-0 transition-all"
                                :class="{
                                    'rotate-180': sortField === 'run_status' && sortDirection === 'desc',
                                    'opacity-0': sortField !== 'run_status'
                                }"
                            />
                        </div>
                    </div>
                    <div class="flex items-center justify-center font-bold text-center">
                        Длительность выгрузки
                    </div>
                    <div class="flex items-center justify-center font-bold text-center">
                        Количество транзакций
                    </div>
                </div>

                <!-- Контейнер для прокрутки строк -->
                <div class="overflow-y-auto max-h-96">
                    <div
                        class="w-full grid grid-cols-6 gap-4 items-center h-12 px-6 even:bg-gray-50"
                        v-for="bundle in sortedBundles"
                        :key="bundle.id"
                    >
                        <div class="text-center">
                            {{ format(new Date(bundle.started_at), 'd MMMM yyyy, HH:mm', { locale: ru }) }}
                        </div>
                        <div class="text-center">
                            {{ bundle.connection_id }}
                        </div>
                        <div class="text-center">
                            {{ bundle.connection_name }}
                        </div>
                        <div class="text-center">
                            {{ bundle.run_status }}
                        </div>
                        <div class="text-center">
                            {{ bundle.total_processing_time }} <span>секунд</span>
                        </div>
                        <div class="text-center">
                            {{ bundle.total_transactions }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>

</style>